import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    ContactInformations,
    ContactInformationsCurrentUserType,
    ModalContext,
} from '@europrocurement/flexy-components';
import {
    ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    ContactTiersReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import {
    ContactTiersRead,
    PrescripteurApi,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import { faUserPlus } from '@fortawesome/sharp-regular-svg-icons';
import {
    useModalMail,
    ManageAvatar,
    useUserContactCard,
    useResolveUserByKeycloakId,
} from '@europrocurement/l2d-modules';
import { UseKeycloakService, KeycloakHasRole, REALM_INTERN } from '@europrocurement/l2d-keycloak';
import { enqueueSnackbar } from 'notistack';
import { PrescriberTabProps } from '../sections/types';
import ContactForm from '../../../forms/ContactForm';
import { useContactKeycloak } from '../../../hooks';

export type ContactListProps = {
    contacts: Array<ContactTiersReadNoteGroupTimestampableBlameambleGroup>;
    type: 'active' | 'inactive';
    sectionTitle: string;
} & PrescriberTabProps;

/** Display the firsts contact ( setted by the limit )
 * - List can be extended to display every contacts
 */
const ContactList: React.FunctionComponent<ContactListProps> = function (props) {
    const { prescriber, fetchPrescriber, contacts, sectionTitle, type } = props;

    const { modalActions } = React.useContext(ModalContext);
    const { callUserContactCardModal } = useUserContactCard();
    const { resolveUserByKeycloakId } = useResolveUserByKeycloakId();
    const { showMailModal } = useModalMail();
    const { utilisateurApi } = useUtilisateurService();
    const keycloakService = UseKeycloakService();
    const { enableKeycloakAccess, sendResetPassword, disableKeycloakAccess } = useContactKeycloak();

    const companyRelationship = prescriber.relationsSocietes[0] ?? null;
    const prescriberHasB2BAccess = companyRelationship?.plateformeB2BAutorise ?? false;
    const [currentUserInfos, setCurrentUserInfos] = useState<ContactInformationsCurrentUserType>({
        name: keycloakService.getName(),
        email: keycloakService.getEmail(),
        service: '',
    });

    /**
     * Call contact form modal ( creation or udate )
     * @param contact - contact update form if provided
     */
    const contactForm = (contact?: ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup) => {
        const afterSubmit = () => {
            modalActions.close();
            fetchPrescriber();
        };
        modalActions.call(
            <ContactForm
                prescriber={prescriber as PrescripteurApi}
                afterSubmit={afterSubmit}
                contact={contact}
            />,
        );
    };

    const toggleKeycloakAccess = async (
        contact: ContactTiersRead,
        userIdentifier: string | undefined,
    ): Promise<string | undefined> => {
        const executor = async (
            resolve: (hasIdentifier: string | undefined) => void,
            reject: (e?: unknown) => void,
        ) => {
            const contactId = contact.id;

            if (!contactId) {
                return reject();
            }

            try {
                if (userIdentifier) {
                    await disableKeycloakAccess(contact);
                    enqueueSnackbar("L'accès a bien été supprimé");

                    return resolve(undefined);
                }
                const newUserIdentifier = await enableKeycloakAccess(contact);
                enqueueSnackbar("L'accès a bien été créé");

                return resolve(newUserIdentifier);
            } catch (e) {
                console.error(e);

                enqueueSnackbar('Une erreur est survenue lors de la modification du contact', {
                    variant: 'error',
                });

                return reject(e);
            }
        };

        return new Promise(executor);
    };

    const handleSendResetPassword = async (contact: ContactTiersRead): Promise<void> => {
        const executor = async (resolve: () => void, reject: (e?: unknown) => void) => {
            const contactId = contact.id;

            if (!contactId) {
                return reject();
            }

            try {
                await sendResetPassword(contact);
                enqueueSnackbar("L'email de mot de passe oublié a bien été renvoyé");

                return resolve();
            } catch (e) {
                console.error(e);

                enqueueSnackbar("Une erreur est survenue lors de l'envoi de l'email", {
                    variant: 'error',
                });

                return reject(e);
            }
        };

        return new Promise(executor);
    };

    // Temporary until keycloackService has a property in wich service the current user work
    useEffect(() => {
        utilisateurApi
            .apiUtilisateursGetCollection({
                mail: keycloakService.getEmail(),
            })
            .then((res) => {
                if (res.data && res.data['hydra:member'].length > 0) {
                    const user = res.data['hydra:member'][0];
                    setCurrentUserInfos({
                        ...currentUserInfos,
                        service: user.service?.libelle || '',
                    });
                }
            })
            .catch((err) => console.log(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [utilisateurApi, keycloakService]);

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                minWidth="100%"
                marginTop="10px"
            >
                <Typography
                    variant="l2dh7"
                    sx={{ marginY: '8px' }}
                >
                    {sectionTitle}
                </Typography>

                {type === 'active' && (
                    <IconButton
                        onClick={() => contactForm()}
                        sx={{ borderRadius: '5px', marginBottom: '0.5em' }}
                    >
                        <Box sx={{ display: 'flex', columnGap: '13px', alignItems: 'center' }}>
                            <FaOptionIcon
                                icon={faUserPlus}
                                sx={{ opacity: 0.8, fontSize: '14px' }}
                            />
                            <Typography>Ajouter un contact</Typography>
                        </Box>
                    </IconButton>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    gap: '16px',
                }}
            >
                {contacts &&
                    contacts.map((contact, index) => {
                        const key = `${type}_contacts_${index}`;
                        return (
                            <ContactInformations
                                key={key}
                                contact={contact}
                                openForm={contactForm}
                                prescriberHasB2BAccess={prescriberHasB2BAccess}
                                resolveUserInfo={resolveUserByKeycloakId}
                                manageAvatar={
                                    contact.userIdentifier !== undefined &&
                                    contact.userIdentifier !== null ? (
                                        <KeycloakHasRole
                                            kcRole={REALM_INTERN}
                                            elseChildren={undefined}
                                        >
                                            <ManageAvatar
                                                overwriteUser={{
                                                    firstName: contact.prenomContact,
                                                    lastName: contact.nomContact,
                                                    username: contact.userIdentifier,
                                                    email: contact.emailsContact[0]?.email,
                                                }}
                                            />
                                        </KeycloakHasRole>
                                    ) : undefined
                                }
                                showMailModal={showMailModal}
                                currentUserInfos={currentUserInfos}
                                onToggleKeycloakAccess={toggleKeycloakAccess}
                                onSendResetPassword={handleSendResetPassword}
                                callUserContactCardModal={callUserContactCardModal}
                            />
                        );
                    })}
            </Box>
        </>
    );
};

export default ContactList;
